/**
 * Platform HQ — Security & Profile center.
 *
 * Self-service for every Scalevisory admin: change password, enable/disable
 * TOTP 2FA, review the last 20 sign-ins. Lives at /platform-admin/profile.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import {
  ShieldCheck, KeyRound, Smartphone, History, ArrowLeft, Check, X,
  AlertTriangle, Copy, Loader2, LogOut, AlertOctagon, Trash2,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function PlatformProfile() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { admin, token, logout } = usePlatformAuth();

  useEffect(() => { if (!admin) navigate('/platform-admin/signin'); }, [admin, navigate]);

  const headers = { Authorization: `Bearer ${token}` };

  // Password
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  // TOTP
  const [setup, setSetup] = useState(null);          // { secret, qr_data_url, provisioning_uri }
  const [totpCode, setTotpCode] = useState('');
  const [totpSaving, setTotpSaving] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableSaving, setDisableSaving] = useState(false);

  // Login history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Danger zone — wipe customer data (owner-only)
  const [showWipe, setShowWipe] = useState(false);
  const [wipePassword, setWipePassword] = useState('');
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [wipeRunning, setWipeRunning] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);

  const refresh = async () => {
    try {
      setHistoryLoading(true);
      const [h] = await Promise.all([
        axios.get(`${API}/platform/auth/login-history`, { headers }),
      ]);
      setHistory(h.data || []);
    } catch (err) {
      toast({
        title: 'Could not load sign-in history',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { if (token) refresh(); /* eslint-disable-line */ }, [token]);

  // ── Change password ────────────────────────────────────────────────────
  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/platform/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.next,
      }, { headers });
      toast({ title: 'Password updated', description: 'Use the new password on your next sign-in.' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast({
        title: 'Could not change password',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setPwSaving(false);
    }
  };

  // ── TOTP enrollment ────────────────────────────────────────────────────
  const startTotpSetup = async () => {
    setTotpSaving(true);
    try {
      const res = await axios.post(`${API}/platform/auth/totp/setup`, {}, { headers });
      setSetup(res.data);
      setTotpCode('');
    } catch (err) {
      toast({
        title: 'Could not start 2FA setup',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setTotpSaving(false);
    }
  };

  const enableTotp = async (e) => {
    e.preventDefault();
    setTotpSaving(true);
    try {
      await axios.post(`${API}/platform/auth/totp/enable`, { code: totpCode }, { headers });
      toast({
        title: '2FA enabled',
        description: 'You\u2019ll be asked for a code on every future sign-in.',
      });
      setSetup(null);
      setTotpCode('');
      // Force a context refresh so the badge flips to "Enabled"
      window.location.reload();
    } catch (err) {
      toast({
        title: 'Code did not match',
        description: err?.response?.data?.detail || 'Try the next code from your app.',
        variant: 'destructive',
      });
    } finally {
      setTotpSaving(false);
    }
  };

  const disableTotp = async () => {
    setDisableSaving(true);
    try {
      await axios.post(`${API}/platform/auth/totp/disable`, { current_password: disablePassword }, { headers });
      toast({ title: '2FA disabled', description: 'Your account is now password-only.' });
      setShowDisable(false);
      setDisablePassword('');
      window.location.reload();
    } catch (err) {
      toast({
        title: 'Could not disable 2FA',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setDisableSaving(false);
    }
  };

  const copySecret = () => {
    if (!setup?.secret) return;
    navigator.clipboard?.writeText(setup.secret);
    toast({ title: 'Secret copied' });
  };

  // ── Danger zone: wipe all HinesTime customer data (owner only) ─────────
  const runWipe = async () => {
    setWipeRunning(true);
    try {
      const res = await axios.post(
        `${API}/platform/danger/reset-customer-data`,
        { current_password: wipePassword, confirmation: wipeConfirm },
        { headers },
      );
      setWipeResult(res.data);
      toast({
        title: 'Customer data wiped',
        description: `${res.data.wiped?.length || 0} collections cleared. Platform HQ access intact.`,
      });
      // Refresh history to show the audit entry, but leave dialog open so user sees the result
      refresh();
      setWipePassword('');
      setWipeConfirm('');
    } catch (err) {
      toast({
        title: 'Wipe failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setWipeRunning(false);
    }
  };

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="platform-profile">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/platform-admin/dashboard')} className="text-slate-300 hover:text-white" data-testid="profile-back-btn">
              <ArrowLeft className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Dashboard</span>
            </Button>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                 style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}>
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate">Security &amp; Profile</h1>
              <p className="text-xs text-slate-400 truncate">{admin.email} · {admin.role}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/platform-admin/signin'); }} className="text-slate-300 hover:text-white" data-testid="profile-logout-btn">
            <LogOut className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Sign out</span>
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Account info */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-100 mb-4">Account</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div><dt className="text-slate-400 text-xs uppercase tracking-wider">Name</dt><dd className="mt-1">{admin.name}</dd></div>
              <div><dt className="text-slate-400 text-xs uppercase tracking-wider">Email</dt><dd className="mt-1 break-all">{admin.email}</dd></div>
              <div><dt className="text-slate-400 text-xs uppercase tracking-wider">Role</dt><dd className="mt-1 capitalize">{admin.role}</dd></div>
            </dl>
            <p className="text-xs text-slate-500 mt-4">
              Only <code className="text-slate-300">@scalevisory.com</code> email addresses can hold Platform HQ accounts.
            </p>
          </CardContent>
        </Card>

        {/* Change password */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-100 flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-blue-400" />Change password</h2>
            <p className="text-xs text-slate-400 mb-4">Minimum 12 characters with upper, lower, number, and symbol.</p>
            <form onSubmit={submitPassword} className="space-y-3 max-w-md">
              <div>
                <Label htmlFor="pw-current" className="text-slate-300">Current password</Label>
                <Input id="pw-current" type="password" autoComplete="current-password" required
                       value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                       className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="pw-current" />
              </div>
              <div>
                <Label htmlFor="pw-next" className="text-slate-300">New password</Label>
                <Input id="pw-next" type="password" autoComplete="new-password" required
                       value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })}
                       className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="pw-next" />
              </div>
              <div>
                <Label htmlFor="pw-confirm" className="text-slate-300">Confirm new password</Label>
                <Input id="pw-confirm" type="password" autoComplete="new-password" required
                       value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                       className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="pw-confirm" />
              </div>
              <Button type="submit" disabled={pwSaving}
                      className="text-white font-semibold"
                      style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}
                      data-testid="pw-submit">
                {pwSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : 'Update password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Two-factor authentication */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-100 flex items-center gap-2 mb-1"><Smartphone className="w-4 h-4 text-violet-400" />Two-factor authentication</h2>
                <p className="text-xs text-slate-400">Scan the QR with Google Authenticator, Authy, 1Password, or any TOTP app.</p>
              </div>
              <Badge className={admin.totp_enabled
                ? 'bg-emerald-900 text-emerald-200 border border-emerald-700'
                : 'bg-amber-900 text-amber-200 border border-amber-700'}
                data-testid="totp-status">
                {admin.totp_enabled ? <Check className="w-3.5 h-3.5 mr-1" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
                {admin.totp_enabled ? 'Enabled' : 'Not enabled'}
              </Badge>
            </div>

            {admin.totp_enabled ? (
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-sm text-slate-300 flex-1">
                  You\u2019re asked for a 6-digit code on every sign-in. Lost your device?
                  Disable 2FA below (you\u2019ll need your password) and re-enroll on a new device.
                </p>
                <Button variant="outline" onClick={() => setShowDisable(true)}
                        className="border-red-800 text-red-300 hover:bg-red-950/40" data-testid="totp-disable-btn">
                  <X className="w-4 h-4 mr-1.5" />Disable 2FA
                </Button>
              </div>
            ) : !setup ? (
              <div className="mt-4">
                <Button onClick={startTotpSetup} disabled={totpSaving}
                        className="text-white font-semibold"
                        style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}
                        data-testid="totp-setup-btn">
                  {totpSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>) : 'Set up 2FA'}
                </Button>
              </div>
            ) : (
              <div className="mt-4 grid sm:grid-cols-2 gap-5">
                <div className="bg-white p-3 rounded-md self-start" data-testid="totp-qr-wrapper">
                  <img src={setup.qr_data_url} alt="2FA QR" className="w-44 h-44" data-testid="totp-qr-img" />
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Can\u2019t scan? Enter this secret manually:</p>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-slate-800 border border-slate-700 px-2 py-1.5 rounded break-all flex-1" data-testid="totp-secret">{setup.secret}</code>
                      <Button size="sm" variant="ghost" onClick={copySecret} className="text-slate-300 hover:text-white" data-testid="totp-copy-secret">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <form onSubmit={enableTotp} className="space-y-2">
                    <Label htmlFor="totp-code" className="text-slate-300">Enter the 6-digit code from your app</Label>
                    <Input id="totp-code" inputMode="numeric" pattern="\d{6}" maxLength={6} required
                           value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                           placeholder="123456"
                           className="bg-slate-800 border-slate-700 text-white text-lg tracking-widest font-mono"
                           data-testid="totp-verify-code" />
                    <div className="flex gap-2">
                      <Button type="submit" disabled={totpSaving || totpCode.length !== 6}
                              className="text-white font-semibold"
                              style={{ background: 'linear-gradient(135deg, #1E88E5, #2CA01C)' }}
                              data-testid="totp-verify-submit">
                        {totpSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</>) : 'Verify & enable'}
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => { setSetup(null); setTotpCode(''); }}
                              className="text-slate-300 hover:text-white" data-testid="totp-cancel-setup">
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sign-in history */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-100 flex items-center gap-2 mb-1"><History className="w-4 h-4 text-blue-400" />Sign-in history</h2>
            <p className="text-xs text-slate-400 mb-4">Most recent 20 sign-in attempts on your account. We email you on every successful sign-in too.</p>
            {historyLoading ? (
              <div className="flex items-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</div>
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-500">No sign-ins recorded yet.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="login-history-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 pr-3">Result</th>
                      <th className="py-2 pr-3">2FA</th>
                      <th className="py-2 pr-3">IP</th>
                      <th className="py-2 pr-3">Browser</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} className="border-b border-slate-800/60">
                        <td className="py-2 pr-3 whitespace-nowrap text-slate-300">{fmtWhen(h.at)}</td>
                        <td className="py-2 pr-3">
                          {h.success
                            ? <span className="text-emerald-400 inline-flex items-center"><Check className="w-3.5 h-3.5 mr-1" />Success</span>
                            : <span className="text-red-400 inline-flex items-center"><X className="w-3.5 h-3.5 mr-1" />Failed</span>}
                        </td>
                        <td className="py-2 pr-3 text-slate-400">{h.totp_used ? 'Yes' : 'No'}</td>
                        <td className="py-2 pr-3 text-slate-400 font-mono text-xs">{h.ip || '—'}</td>
                        <td className="py-2 pr-3 text-slate-400 text-xs truncate max-w-xs" title={h.user_agent}>{h.user_agent || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* DANGER ZONE — owner only */}
        {admin.role === 'owner' && (
          <Card className="bg-slate-900 border-red-900/70" data-testid="danger-zone-card">
            <CardContent className="p-5">
              <h2 className="font-semibold text-red-300 flex items-center gap-2 mb-1">
                <AlertOctagon className="w-4 h-4" />Danger zone
              </h2>
              <p className="text-xs text-slate-400 mb-4">
                Owner-only actions. Use these to prepare HinesTime for a fresh public launch.
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-md border border-red-900/50 bg-red-950/30">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-100">Wipe all customer data</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Drops every <strong>HinesTime</strong> company, user, job, customer, team, notification, schedule, and audit row.
                    Preserves Platform HQ admins so you keep access. This cannot be undone.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => { setShowWipe(true); setWipeResult(null); }}
                  className="border-red-800 text-red-300 hover:bg-red-950/60 flex-shrink-0"
                  data-testid="wipe-open-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />Wipe customer data…
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Disable 2FA confirm dialog */}
      <Dialog open={showDisable} onOpenChange={(o) => { if (!o) { setShowDisable(false); setDisablePassword(''); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="totp-disable-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" />Disable 2FA?</DialogTitle>
            <DialogDescription className="text-slate-400">
              Your account will be protected by password only. Re-enter your password to confirm.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="disable-pw" className="text-slate-300">Current password</Label>
            <Input id="disable-pw" type="password" required autoComplete="current-password"
                   value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)}
                   className="mt-1 bg-slate-800 border-slate-700 text-white" data-testid="totp-disable-password" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowDisable(false); setDisablePassword(''); }} disabled={disableSaving} data-testid="totp-disable-cancel">Cancel</Button>
            <Button onClick={disableTotp} disabled={disableSaving || !disablePassword}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="totp-disable-confirm">
              {disableSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Disabling…</>) : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DANGER ZONE — wipe customer data dialog */}
      <Dialog open={showWipe} onOpenChange={(o) => {
        if (!o) { setShowWipe(false); setWipePassword(''); setWipeConfirm(''); setWipeResult(null); }
      }}>
        <DialogContent className="bg-slate-900 border-red-900/60 text-slate-100 max-w-lg" data-testid="wipe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <AlertOctagon className="w-5 h-5" />Wipe all customer data
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will permanently delete every HinesTime company, user, job, and customer record.
              Platform HQ admins are preserved. Type the confirmation exactly to enable the button.
            </DialogDescription>
          </DialogHeader>

          {!wipeResult ? (
            <div className="space-y-3">
              <div>
                <Label htmlFor="wipe-pw" className="text-slate-300">Your current Platform HQ password</Label>
                <Input
                  id="wipe-pw" type="password" autoComplete="current-password" required
                  value={wipePassword} onChange={(e) => setWipePassword(e.target.value)}
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                  data-testid="wipe-password"
                />
              </div>
              <div>
                <Label htmlFor="wipe-confirm" className="text-slate-300">
                  Type <code className="bg-slate-800 px-1.5 py-0.5 rounded text-red-300">WIPE ALL CUSTOMER DATA</code>
                </Label>
                <Input
                  id="wipe-confirm" type="text" required
                  value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)}
                  className="mt-1 bg-slate-800 border-slate-700 text-white font-mono"
                  data-testid="wipe-confirm"
                  placeholder="WIPE ALL CUSTOMER DATA"
                />
              </div>
              <p className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900/50 rounded p-2">
                There is no undo. Make sure you have backups if any production data must be retained.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="wipe-result">
              <p className="text-sm text-emerald-400">
                Wipe complete at {new Date(wipeResult.at).toLocaleString()}
              </p>
              <div className="text-xs text-slate-300">
                <p className="font-semibold mb-1 text-slate-200">Cleared collections ({wipeResult.wiped?.length || 0}):</p>
                <ul className="space-y-0.5">
                  {wipeResult.wiped?.map((w) => (
                    <li key={w.collection} className="flex justify-between font-mono">
                      <span>{w.collection}</span>
                      <span className="text-slate-500">{w.removed} rows</span>
                    </li>
                  ))}
                </ul>
              </div>
              {wipeResult.preserved?.length > 0 && (
                <div className="text-xs text-slate-400">
                  <p className="font-semibold mb-1 text-slate-300">Preserved:</p>
                  <p className="font-mono">{wipeResult.preserved.join(', ')}</p>
                </div>
              )}
              {wipeResult.failed?.length > 0 && (
                <div className="text-xs text-red-400">
                  <p className="font-semibold mb-1">Failures:</p>
                  <ul className="font-mono">
                    {wipeResult.failed.map((f, i) => <li key={i}>{f.collection}: {f.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!wipeResult ? (
              <>
                <Button variant="ghost" onClick={() => { setShowWipe(false); setWipePassword(''); setWipeConfirm(''); }}
                        disabled={wipeRunning} data-testid="wipe-cancel">Cancel</Button>
                <Button onClick={runWipe}
                        disabled={wipeRunning || !wipePassword || wipeConfirm.trim() !== 'WIPE ALL CUSTOMER DATA'}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="wipe-confirm-btn">
                  {wipeRunning
                    ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wiping…</>)
                    : (<><Trash2 className="w-4 h-4 mr-2" />Wipe now</>)}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setShowWipe(false); setWipeResult(null); }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wipe-done">
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
